import React, { useState } from 'react';
import { FormField, TextInput, SelectInput, RadioGroup, NavButtons } from './QuoteFormField';

// Helper to update nested coverage data
function useCoverageData(form, onChange, key) {
  const data = form[key] || {};
  const update = (updates) => onChange({ [key]: { ...data, ...updates } });
  return [data, update];
}

// ---- Personal Auto ----
function AutoForm({ form, onChange }) {
  const [data, update] = useCoverageData(form, onChange, 'form_data_auto');

  const vehicles = data.vehicles || [{}];
  const drivers = data.drivers || [{}];
  const coverage = data.coverage_preferences || {};

  const updateVehicle = (i, updates) => {
    const v = [...vehicles];
    v[i] = { ...v[i], ...updates };
    update({ vehicles: v });
  };
  const addVehicle = () => vehicles.length < 4 && update({ vehicles: [...vehicles, {}] });
  const removeVehicle = (i) => update({ vehicles: vehicles.filter((_, idx) => idx !== i) });

  const updateDriver = (i, updates) => {
    const d = [...drivers];
    d[i] = { ...d[i], ...updates };
    update({ drivers: d });
  };
  const addDriver = () => drivers.length < 4 && update({ drivers: [...drivers, {}] });
  const removeDriver = (i) => update({ drivers: drivers.filter((_, idx) => idx !== i) });

  const updateCoverage = (updates) => update({ coverage_preferences: { ...coverage, ...updates } });

  return (
    <div className="space-y-8">
      <section>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900">Vehicles</h3>
          {vehicles.length < 4 && (
            <button onClick={addVehicle} className="text-sm text-[#1e3a5f] font-medium hover:underline">+ Add Vehicle</button>
          )}
        </div>
        <div className="space-y-6">
          {vehicles.map((v, i) => (
            <div key={i} className="bg-gray-50 rounded-xl p-4 space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">Vehicle {i + 1}</span>
                {i > 0 && <button onClick={() => removeVehicle(i)} className="text-xs text-red-500 hover:underline">Remove</button>}
              </div>
              <div className="grid grid-cols-3 gap-3">
                <FormField label="Year" required><TextInput value={v.year || ''} onChange={val => updateVehicle(i, { year: val })} placeholder="2022" /></FormField>
                <FormField label="Make" required><TextInput value={v.make || ''} onChange={val => updateVehicle(i, { make: val })} placeholder="Toyota" /></FormField>
                <FormField label="Model" required><TextInput value={v.model || ''} onChange={val => updateVehicle(i, { model: val })} placeholder="Camry" /></FormField>
              </div>
              <FormField label="VIN (17 characters)">
                <TextInput value={v.vin || ''} onChange={val => updateVehicle(i, { vin: val })} placeholder="1HGBH41JXMN109186" maxLength={17} />
              </FormField>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Primary Use">
                  <SelectInput value={v.primary_use || ''} onChange={val => updateVehicle(i, { primary_use: val })} options={['Pleasure', 'Commute', 'Business', 'Rideshare']} placeholder="Select use" />
                </FormField>
                <FormField label="Annual Mileage">
                  <TextInput value={v.annual_mileage || ''} onChange={val => updateVehicle(i, { annual_mileage: val })} placeholder="12,000" />
                </FormField>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Ownership">
                  <SelectInput value={v.ownership || ''} onChange={val => updateVehicle(i, { ownership: val })} options={['Own', 'Finance', 'Lease']} placeholder="Select" />
                </FormField>
                {(v.ownership === 'Finance' || v.ownership === 'Lease') && (
                  <FormField label="Lienholder Name">
                    <TextInput value={v.lienholder || ''} onChange={val => updateVehicle(i, { lienholder: val })} placeholder="Wells Fargo" />
                  </FormField>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900">Drivers</h3>
          {drivers.length < 4 && (
            <button onClick={addDriver} className="text-sm text-[#1e3a5f] font-medium hover:underline">+ Add Driver</button>
          )}
        </div>
        <div className="space-y-6">
          {drivers.map((d, i) => (
            <div key={i} className="bg-gray-50 rounded-xl p-4 space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">Driver {i + 1}</span>
                {i > 0 && <button onClick={() => removeDriver(i)} className="text-xs text-red-500 hover:underline">Remove</button>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Full Name"><TextInput value={d.full_name || ''} onChange={val => updateDriver(i, { full_name: val })} placeholder="Maria Santos" /></FormField>
                <FormField label="Date of Birth"><TextInput type="date" value={d.dob || ''} onChange={val => updateDriver(i, { dob: val })} /></FormField>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="License Number"><TextInput value={d.license_number || ''} onChange={val => updateDriver(i, { license_number: val })} /></FormField>
                <FormField label="License State"><TextInput value={d.license_state || ''} onChange={val => updateDriver(i, { license_state: val })} placeholder="CA" maxLength={2} /></FormField>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Year First Licensed"><TextInput value={d.year_first_licensed || ''} onChange={val => updateDriver(i, { year_first_licensed: val })} placeholder="2008" /></FormField>
                <FormField label="Relationship to Applicant"><SelectInput value={d.relationship || ''} onChange={val => updateDriver(i, { relationship: val })} options={['Self', 'Spouse', 'Child', 'Parent', 'Other']} placeholder="Select" /></FormField>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h3 className="font-semibold text-gray-900 mb-4">Coverage Preferences</h3>
        <div className="space-y-4">
          <FormField label="Liability Limits">
            <SelectInput value={coverage.liability_limits || ''} onChange={val => updateCoverage({ liability_limits: val })} options={['30/60', '50/100', '100/300', '250/500']} placeholder="Select limits" />
          </FormField>
          <FormField label="Deductible Preference">
            <SelectInput value={coverage.deductible || ''} onChange={val => updateCoverage({ deductible: val })} options={['$250', '$500', '$1,000', 'N/A']} placeholder="Select deductible" />
          </FormField>
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Comprehensive"><RadioGroup value={coverage.comprehensive || ''} onChange={val => updateCoverage({ comprehensive: val })} options={[{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }]} /></FormField>
            <FormField label="Collision"><RadioGroup value={coverage.collision || ''} onChange={val => updateCoverage({ collision: val })} options={[{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }]} /></FormField>
            <FormField label="Uninsured Motorist"><RadioGroup value={coverage.uninsured_motorist || ''} onChange={val => updateCoverage({ uninsured_motorist: val })} options={[{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }]} /></FormField>
          </div>
        </div>
      </section>
    </div>
  );
}

// ---- Homeowners ----
function HomeForm({ form, onChange }) {
  const [data, update] = useCoverageData(form, onChange, 'form_data_home');

  return (
    <div className="space-y-4">
      <FormField label="Property Address (if different from mailing)">
        <TextInput value={data.property_address || ''} onChange={val => update({ property_address: val })} placeholder="Leave blank if same as mailing address" />
      </FormField>
      <div className="grid grid-cols-2 gap-4">
        <FormField label="Year Built" required><TextInput value={data.year_built || ''} onChange={val => update({ year_built: val })} placeholder="1998" /></FormField>
        <FormField label="Square Footage" required><TextInput value={data.sq_footage || ''} onChange={val => update({ sq_footage: val })} placeholder="1,850" /></FormField>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <FormField label="Number of Stories"><TextInput value={data.stories || ''} onChange={val => update({ stories: val })} placeholder="2" /></FormField>
        <FormField label="Construction Type">
          <SelectInput value={data.construction_type || ''} onChange={val => update({ construction_type: val })} options={['Frame', 'Masonry', 'Masonry Veneer', 'Superior']} placeholder="Select type" />
        </FormField>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <FormField label="Roof Material">
          <SelectInput value={data.roof_material || ''} onChange={val => update({ roof_material: val })} options={['Asphalt', 'Metal', 'Tile', 'Wood Shake', 'Other']} placeholder="Select material" />
        </FormField>
        <FormField label="Year Roof Last Replaced"><TextInput value={data.roof_year || ''} onChange={val => update({ roof_year: val })} placeholder="2019" /></FormField>
      </div>
      <FormField label="Occupancy">
        <SelectInput value={data.occupancy || ''} onChange={val => update({ occupancy: val })} options={['Primary', 'Secondary', 'Tenant-Occupied', 'Vacant']} placeholder="Select occupancy" />
      </FormField>
      <div className="grid grid-cols-3 gap-4">
        <FormField label="Swimming Pool"><RadioGroup value={data.pool || ''} onChange={val => update({ pool: val })} options={[{value:'Yes',label:'Yes'},{value:'No',label:'No'}]} /></FormField>
        <FormField label="Trampoline"><RadioGroup value={data.trampoline || ''} onChange={val => update({ trampoline: val })} options={[{value:'Yes',label:'Yes'},{value:'No',label:'No'}]} /></FormField>
        <FormField label="Dog on Premises"><RadioGroup value={data.dog || ''} onChange={val => update({ dog: val })} options={[{value:'Yes',label:'Yes'},{value:'No',label:'No'}]} /></FormField>
      </div>
      {data.dog === 'Yes' && (
        <FormField label="Dog Breed"><TextInput value={data.dog_breed || ''} onChange={val => update({ dog_breed: val })} placeholder="Labrador" /></FormField>
      )}
      <FormField label="Estimated Dwelling Replacement Value ($)" required>
        <TextInput value={data.dwelling_value || ''} onChange={val => update({ dwelling_value: val })} placeholder="650,000" />
      </FormField>
      <div className="grid grid-cols-2 gap-4">
        <FormField label="Mortgage Lender Name"><TextInput value={data.mortgage_lender || ''} onChange={val => update({ mortgage_lender: val })} placeholder="Wells Fargo" /></FormField>
        <FormField label="Loan Number"><TextInput value={data.loan_number || ''} onChange={val => update({ loan_number: val })} placeholder="123456789" /></FormField>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <FormField label="Burglar Alarm"><RadioGroup value={data.burglar_alarm || ''} onChange={val => update({ burglar_alarm: val })} options={[{value:'Yes',label:'Yes'},{value:'No',label:'No'}]} /></FormField>
        <FormField label="Smoke Detectors"><RadioGroup value={data.smoke_detectors || ''} onChange={val => update({ smoke_detectors: val })} options={[{value:'Yes',label:'Yes'},{value:'No',label:'No'}]} /></FormField>
        <FormField label="Sprinkler System"><RadioGroup value={data.sprinkler || ''} onChange={val => update({ sprinkler: val })} options={[{value:'Yes',label:'Yes'},{value:'No',label:'No'}]} /></FormField>
      </div>
    </div>
  );
}

// ---- Renters ----
function RentersForm({ form, onChange }) {
  const [data, update] = useCoverageData(form, onChange, 'form_data_renters');
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <FormField label="Property Address"><TextInput value={data.property_address || ''} onChange={val => update({ property_address: val })} placeholder="123 Main St" /></FormField>
        <FormField label="Unit Number"><TextInput value={data.unit_number || ''} onChange={val => update({ unit_number: val })} placeholder="Apt 4B" /></FormField>
      </div>
      <FormField label="Type of Building">
        <SelectInput value={data.building_type || ''} onChange={val => update({ building_type: val })} options={['Apartment', 'Condo', 'House', 'Other']} placeholder="Select type" />
      </FormField>
      <FormField label="Number of Roommates"><TextInput value={data.roommates || ''} onChange={val => update({ roommates: val })} placeholder="0" type="number" /></FormField>
      <FormField label="Personal Property Value Estimate ($)"><TextInput value={data.property_value || ''} onChange={val => update({ property_value: val })} placeholder="15,000" /></FormField>
      <FormField label="High-Value Items (jewelry, electronics, etc.)">
        <RadioGroup value={data.high_value_items || ''} onChange={val => update({ high_value_items: val })} options={[{value:'Yes',label:'Yes'},{value:'No',label:'No'}]} />
      </FormField>
      {data.high_value_items === 'Yes' && (
        <FormField label="Describe high-value items and estimated value">
          <textarea value={data.high_value_description || ''} onChange={e => update({ high_value_description: e.target.value })} rows={2} className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f] resize-none" />
        </FormField>
      )}
      <FormField label="Liability Limit Preference">
        <SelectInput value={data.liability_limit || ''} onChange={val => update({ liability_limit: val })} options={['$100,000', '$300,000']} placeholder="Select limit" />
      </FormField>
    </div>
  );
}

// ---- BOP ----
function BOPForm({ form, onChange }) {
  const [data, update] = useCoverageData(form, onChange, 'form_data_bop');
  const missingBusinessInfo = !form.business_legal_name?.trim() || !form.business_description?.trim();
  return (
    <div className="space-y-4">
      {missingBusinessInfo && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
          <strong>Action needed:</strong> Business Legal Name and Business Description are required for a BOP quote. Please go back to Step 2 to complete them.
        </div>
      )}
      <FormField label="Business Location Address (if different from mailing)"><TextInput value={data.location_address || ''} onChange={val => update({ location_address: val })} /></FormField>
      <div className="grid grid-cols-2 gap-4">
        <FormField label="Year Built"><TextInput value={data.year_built || ''} onChange={val => update({ year_built: val })} placeholder="2005" /></FormField>
        <FormField label="Sq Ft Occupied"><TextInput value={data.sq_footage || ''} onChange={val => update({ sq_footage: val })} placeholder="2,500" /></FormField>
      </div>
      <FormField label="Construction Type">
        <SelectInput value={data.construction_type || ''} onChange={val => update({ construction_type: val })} options={['Frame', 'Masonry', 'Masonry Non-Combustible', 'Fire Resistive']} placeholder="Select type" />
      </FormField>
      <FormField label="Occupancy">
        <SelectInput value={data.occupancy || ''} onChange={val => update({ occupancy: val })} options={['Owner-Occupied', 'Tenant']} placeholder="Select" />
      </FormField>
      <div className="grid grid-cols-2 gap-4">
        <FormField label="Annual Gross Revenue ($)" required><TextInput value={data.annual_revenue || ''} onChange={val => update({ annual_revenue: val })} placeholder="500,000" /></FormField>
        <FormField label="Number of Employees"><TextInput value={data.employees || ''} onChange={val => update({ employees: val })} type="number" /></FormField>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <FormField label="Building Limit (if owner) ($)"><TextInput value={data.building_limit || ''} onChange={val => update({ building_limit: val })} /></FormField>
        <FormField label="Contents / BPP Limit ($)"><TextInput value={data.bpp_limit || ''} onChange={val => update({ bpp_limit: val })} /></FormField>
      </div>
      <FormField label="Liability Limit">
        <SelectInput value={data.liability_limit || ''} onChange={val => update({ liability_limit: val })} options={['$300,000', '$600,000', '$1,000,000', '$2,000,000']} placeholder="Select limit" />
      </FormField>
      <div className="grid grid-cols-2 gap-4">
        <FormField label="Sprinkler System"><RadioGroup value={data.sprinkler || ''} onChange={val => update({ sprinkler: val })} options={[{value:'Yes',label:'Yes'},{value:'No',label:'No'}]} /></FormField>
        <FormField label="Alarm System"><RadioGroup value={data.alarm || ''} onChange={val => update({ alarm: val })} options={[{value:'Yes',label:'Yes'},{value:'No',label:'No'}]} /></FormField>
      </div>
    </div>
  );
}

// ---- CGL ----
function CGLForm({ form, onChange }) {
  const [data, update] = useCoverageData(form, onChange, 'form_data_cgl');
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <FormField label="Each Occurrence Limit">
          <SelectInput value={data.occurrence_limit || ''} onChange={val => update({ occurrence_limit: val })} options={['$300,000', '$500,000', '$1,000,000', '$2,000,000']} placeholder="Select" />
        </FormField>
        <FormField label="General Aggregate Limit">
          <SelectInput value={data.aggregate_limit || ''} onChange={val => update({ aggregate_limit: val })} options={['$600,000', '$1,000,000', '$2,000,000', '$4,000,000']} placeholder="Select" />
        </FormField>
      </div>
      <FormField label="Coverage Trigger">
        <SelectInput value={data.coverage_trigger || ''} onChange={val => update({ coverage_trigger: val })} options={['Occurrence', 'Claims-Made']} placeholder="Select" />
      </FormField>
      <div className="grid grid-cols-2 gap-4">
        <FormField label="Annual Gross Revenue ($)"><TextInput value={data.annual_revenue || ''} onChange={val => update({ annual_revenue: val })} /></FormField>
        <FormField label="Annual Payroll ($)"><TextInput value={data.annual_payroll || ''} onChange={val => update({ annual_payroll: val })} /></FormField>
      </div>
      <FormField label="Number of Employees"><TextInput value={data.employees || ''} onChange={val => update({ employees: val })} type="number" /></FormField>
      <FormField label="Subcontracted work?"><RadioGroup value={data.subcontracted || ''} onChange={val => update({ subcontracted: val })} options={[{value:'Yes',label:'Yes'},{value:'No',label:'No'}]} /></FormField>
      {data.subcontracted === 'Yes' && (
        <div className="grid grid-cols-2 gap-4">
          <FormField label="% Subcontracted"><TextInput value={data.sub_pct || ''} onChange={val => update({ sub_pct: val })} placeholder="20%" /></FormField>
          <FormField label="Subs carry own GL?"><RadioGroup value={data.subs_have_gl || ''} onChange={val => update({ subs_have_gl: val })} options={[{value:'Yes',label:'Yes'},{value:'No',label:'No'}]} /></FormField>
        </div>
      )}
      <FormField label="Liquor served on premises?"><RadioGroup value={data.liquor || ''} onChange={val => update({ liquor: val })} options={[{value:'Yes',label:'Yes'},{value:'No',label:'No'}]} /></FormField>
      <FormField label="Any professional services?"><RadioGroup value={data.professional_services || ''} onChange={val => update({ professional_services: val })} options={[{value:'Yes',label:'Yes'},{value:'No',label:'No'}]} /></FormField>
    </div>
  );
}

// ---- Workers Comp ----
function WCForm({ form, onChange }) {
  const [data, update] = useCoverageData(form, onChange, 'form_data_wc');
  const classes = data.class_codes || [{}];

  const updateClass = (i, updates) => {
    const c = [...classes];
    c[i] = { ...c[i], ...updates };
    update({ class_codes: c });
  };

  return (
    <div className="space-y-4">
      <FormField label="Employer's Liability Limit">
        <SelectInput value={data.el_limit || ''} onChange={val => update({ el_limit: val })} options={['$100,000', '$500,000', '$1,000,000']} placeholder="Select limit" />
      </FormField>
      <FormField label="Total Annual Payroll ($)"><TextInput value={data.total_payroll || ''} onChange={val => update({ total_payroll: val })} /></FormField>
      <div className="grid grid-cols-2 gap-4">
        <FormField label="Full-Time Employees"><TextInput value={data.ft_employees || ''} onChange={val => update({ ft_employees: val })} type="number" /></FormField>
        <FormField label="Part-Time Employees"><TextInput value={data.pt_employees || ''} onChange={val => update({ pt_employees: val })} type="number" /></FormField>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-medium text-gray-700">Job Classes</h4>
          {classes.length < 5 && <button onClick={() => update({ class_codes: [...classes, {}] })} className="text-xs text-[#1e3a5f] hover:underline">+ Add Class</button>}
        </div>
        {classes.map((c, i) => (
          <div key={i} className="bg-gray-50 rounded-lg p-3 space-y-2 mb-2">
            <FormField label="Job Description"><TextInput value={c.description || ''} onChange={val => updateClass(i, { description: val })} placeholder="e.g. Office Staff" /></FormField>
            <FormField label="Payroll for this role ($)"><TextInput value={c.payroll || ''} onChange={val => updateClass(i, { payroll: val })} /></FormField>
          </div>
        ))}
      </div>
      <FormField label="Officers included in WC?"><RadioGroup value={data.officers_included || ''} onChange={val => update({ officers_included: val })} options={[{value:'Yes',label:'Yes'},{value:'No',label:'No'}]} /></FormField>
      <FormField label="Experience Mod (X-Mod) if known"><TextInput value={data.xmod || ''} onChange={val => update({ xmod: val })} placeholder="1.00 (optional)" /></FormField>
    </div>
  );
}

// ---- Commercial Auto ----
function CommercialAutoForm({ form, onChange }) {
  const [data, update] = useCoverageData(form, onChange, 'form_data_commercial_auto');
  const vehicles = data.vehicles || [{}];
  const drivers = data.drivers || [{}];

  const updateVehicle = (i, updates) => { const v = [...vehicles]; v[i] = { ...v[i], ...updates }; update({ vehicles: v }); };
  const updateDriver = (i, updates) => { const d = [...drivers]; d[i] = { ...d[i], ...updates }; update({ drivers: d }); };

  return (
    <div className="space-y-6">
      <section>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-900">Vehicles</h3>
          {vehicles.length < 6 && <button onClick={() => update({ vehicles: [...vehicles, {}] })} className="text-sm text-[#1e3a5f] hover:underline">+ Add</button>}
        </div>
        {vehicles.map((v, i) => (
          <div key={i} className="bg-gray-50 rounded-xl p-4 space-y-3 mb-3">
            <div className="grid grid-cols-3 gap-3">
              <FormField label="Year"><TextInput value={v.year||''} onChange={val=>updateVehicle(i,{year:val})} /></FormField>
              <FormField label="Make"><TextInput value={v.make||''} onChange={val=>updateVehicle(i,{make:val})} /></FormField>
              <FormField label="Model"><TextInput value={v.model||''} onChange={val=>updateVehicle(i,{model:val})} /></FormField>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="VIN"><TextInput value={v.vin||''} onChange={val=>updateVehicle(i,{vin:val})} maxLength={17} /></FormField>
              <FormField label="Garaging ZIP"><TextInput value={v.garaging_zip||''} onChange={val=>updateVehicle(i,{garaging_zip:val})} maxLength={5} /></FormField>
            </div>
          </div>
        ))}
      </section>
      <FormField label="Liability CSL Limit">
        <SelectInput value={data.liability_limit||''} onChange={val=>update({liability_limit:val})} options={['$300,000','$500,000','$1,000,000']} placeholder="Select limit" />
      </FormField>
      <FormField label="Hired Auto?"><RadioGroup value={data.hired_auto||''} onChange={val=>update({hired_auto:val})} options={[{value:'Yes',label:'Yes'},{value:'No',label:'No'}]} /></FormField>
      <FormField label="Non-Owned Auto?"><RadioGroup value={data.non_owned||''} onChange={val=>update({non_owned:val})} options={[{value:'Yes',label:'Yes'},{value:'No',label:'No'}]} /></FormField>
      <FormField label="Hazardous materials transported?"><RadioGroup value={data.hazmat||''} onChange={val=>update({hazmat:val})} options={[{value:'Yes',label:'Yes'},{value:'No',label:'No'}]} /></FormField>
      <FormField label="DOT/ICC filing required?"><RadioGroup value={data.dot_filing||''} onChange={val=>update({dot_filing:val})} options={[{value:'Yes',label:'Yes'},{value:'No',label:'No'}]} /></FormField>

      <section>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-900">Drivers</h3>
          {drivers.length < 6 && <button onClick={() => update({ drivers: [...drivers, {}] })} className="text-sm text-[#1e3a5f] hover:underline">+ Add</button>}
        </div>
        {drivers.map((d, i) => (
          <div key={i} className="bg-gray-50 rounded-xl p-4 space-y-3 mb-3">
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Full Name"><TextInput value={d.full_name||''} onChange={val=>updateDriver(i,{full_name:val})} /></FormField>
              <FormField label="Date of Birth"><TextInput type="date" value={d.dob||''} onChange={val=>updateDriver(i,{dob:val})} /></FormField>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <FormField label="License #"><TextInput value={d.license||''} onChange={val=>updateDriver(i,{license:val})} /></FormField>
              <FormField label="State"><TextInput value={d.state||''} onChange={val=>updateDriver(i,{state:val})} maxLength={2} /></FormField>
              <FormField label="Year First Licensed"><TextInput value={d.year_licensed||''} onChange={val=>updateDriver(i,{year_licensed:val})} /></FormField>
            </div>
          </div>
        ))}
      </section>
    </div>
  );
}

// ---- Umbrella ----
function UmbrellaForm({ form, onChange }) {
  const [data, update] = useCoverageData(form, onChange, 'form_data_umbrella');
  return (
    <div className="space-y-4">
      <FormField label="Umbrella Limit Requested">
        <SelectInput value={data.umbrella_limit||''} onChange={val=>update({umbrella_limit:val})} options={['$1,000,000','$2,000,000','$3,000,000','$5,000,000','$10,000,000']} placeholder="Select limit" />
      </FormField>
      <FormField label="Existing Underlying CGL Carrier + Limit"><TextInput value={data.underlying_cgl||''} onChange={val=>update({underlying_cgl:val})} placeholder="e.g. Travelers — $1M" /></FormField>
      <FormField label="Existing Underlying Auto Carrier + Limit"><TextInput value={data.underlying_auto||''} onChange={val=>update({underlying_auto:val})} placeholder="e.g. Progressive — $500K" /></FormField>
      <FormField label="Existing Underlying Employer's Liability Carrier + Limit (if any)"><TextInput value={data.underlying_el||''} onChange={val=>update({underlying_el:val})} placeholder="Optional" /></FormField>
      <FormField label="Any foreign operations?"><RadioGroup value={data.foreign_ops||''} onChange={val=>update({foreign_ops:val})} options={[{value:'Yes',label:'Yes'},{value:'No',label:'No'}]} /></FormField>
      <FormField label="Any known claims that may exceed underlying limits?"><RadioGroup value={data.known_claims||''} onChange={val=>update({known_claims:val})} options={[{value:'Yes',label:'Yes'},{value:'No',label:'No'}]} /></FormField>
    </div>
  );
}

function getValidationErrors(form) {
  const type = form.coverage_type;
  const errors = [];

  if (type === 'Personal Auto') {
    const v = (form.form_data_auto?.vehicles || [])[0] || {};
    if (!v.year) errors.push('Vehicle Year (Vehicle 1)');
    if (!v.make) errors.push('Vehicle Make (Vehicle 1)');
    if (!v.model) errors.push('Vehicle Model (Vehicle 1)');
  }

  if (type === 'Homeowners') {
    const d = form.form_data_home || {};
    if (!d.year_built) errors.push('Year Built');
    if (!d.sq_footage) errors.push('Square Footage');
    if (!d.dwelling_value) errors.push('Estimated Dwelling Replacement Value');
  }

  if (type === 'Business Owners Policy') {
    if (!form.business_legal_name?.trim()) errors.push('Business Legal Name (go back to Step 2)');
    if (!form.business_description?.trim()) errors.push('Business Description (go back to Step 2)');
    if (!form.form_data_bop?.annual_revenue) errors.push('Annual Gross Revenue');
  }

  return errors;
}

const FORM_MAP = {
  'Personal Auto': AutoForm,
  'Homeowners': HomeForm,
  'Renters': RentersForm,
  'Business Owners Policy': BOPForm,
  'Commercial General Liability': CGLForm,
  'Workers Compensation': WCForm,
  'Commercial Auto': CommercialAutoForm,
  'Umbrella': UmbrellaForm,
};

export default function QuoteStepCoverageDetails({ form, onChange, onNext, onBack }) {
  const [errors, setErrors] = useState([]);
  const CoverageForm = FORM_MAP[form.coverage_type];

  const handleNext = () => {
    const errs = getValidationErrors(form);
    if (errs.length > 0) {
      setErrors(errs);
      return;
    }
    setErrors([]);
    onNext();
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-[#1e3a5f] mb-2">{form.coverage_type} Details</h2>
        <p className="text-gray-500">Tell us more about your coverage needs.</p>
      </div>

      {CoverageForm ? (
        <CoverageForm form={form} onChange={onChange} />
      ) : (
        <p className="text-gray-500">No additional details needed for this coverage type.</p>
      )}

      {errors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm font-semibold text-red-700 mb-2">Please complete the following required fields:</p>
          <ul className="list-disc list-inside space-y-1">
            {errors.map((e, i) => (
              <li key={i} className="text-sm text-red-600">{e}</li>
            ))}
          </ul>
        </div>
      )}

      <NavButtons onBack={onBack} onNext={handleNext} />
    </div>
  );
}
